import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpacaService, DailyEdgeResponse, DailyEdgePick, DailyEdgePanel } from '../../services/AlpacaService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './DailyEdge.module.css';

const rsiColor = (v: number | null) => {
  if (v == null) return undefined;
  if (v <= 15) return 'var(--color-positive, #2ecc71)';
  if (v <= 30) return 'rgba(46, 204, 113, 0.6)';
  if (v >= 85) return 'var(--color-negative, #e74c3c)';
  if (v >= 70) return 'rgba(231, 76, 60, 0.6)';
  return undefined;
};

const dirBadge = (dir: string) => (dir === 'LONG' ? styles.badgeLong : dir === 'SHORT' ? styles.badgeShort : '');

const DailyEdge: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DailyEdgeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState<string | null>(null); // 'premarket' | 'power_hour' | null
  const [date, setDate] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await alpacaService.getDailyEdge(date);
        setData(res);
      } catch (e: any) {
        setError(e?.message || 'Failed to load Daily Edge');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [date]);

  const runScan = async (type: 'premarket' | 'power_hour') => {
    setScanning(type);
    setError('');
    try {
      await alpacaService.runDailyEdgeScan(type);
      // Reload results after scan completes
      const res = await alpacaService.getDailyEdge(date);
      setData(res);
    } catch (e: any) {
      setError(e?.message || `${type} scan failed`);
    } finally {
      setScanning(null);
    }
  };

  const renderPanel = (
    title: string,
    subtitle: string,
    panel: DailyEdgePanel | null,
    scanTime: string,
    scanType: 'premarket' | 'power_hour',
  ) => {
    const hasPicks = panel && panel.picks && panel.picks.length > 0;
    const scannedAt = panel?.scannedAt;
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>{title}</h2>
            <p className={styles.panelSub}>{subtitle}</p>
          </div>
          <div className={styles.panelMeta}>
            {scannedAt ? (
              <span className={styles.statusDone}>Scanned {new Date(scannedAt).toLocaleTimeString()}</span>
            ) : (
              <span className={styles.statusPending}>Pending {scanTime}</span>
            )}
            {panel && <span className={styles.countBadge}>{panel.picks?.length || 0} picks</span>}
            <button
              className={styles.scanBtn}
              onClick={() => runScan(scanType)}
              disabled={scanning !== null}
              title={`Rescan all S&P 500 for ${scanType === 'premarket' ? 'morning' : 'power hour'} picks`}
            >
              {scanning === scanType ? 'Scanning...' : 'Rescan'}
            </button>
          </div>
        </div>

        {!hasPicks && (
          <div className={styles.emptyPanel}>
            {scannedAt
              ? 'No qualifying picks this scan — nothing met the confluence + confidence threshold.'
              : `Scan runs at ${scanTime} ET. Check back after it fires.`}
          </div>
        )}

        {hasPicks && (
          <div className={styles.pickGrid}>
            {panel!.picks.map((p: DailyEdgePick) => (
              <div key={p.symbol} className={styles.pickCard}>
                <div className={styles.pickTop}>
                  <span className={styles.pickSymbol}>{p.symbol}</span>
                  <span className={styles.pickSector}>{p.sector}</span>
                  <span className={`${styles.pickDir} ${dirBadge(p.direction)}`}>{p.direction}</span>
                  <span className={styles.pickConf}>{p.confidence}%</span>
                </div>
                <div className={styles.pickRsi}>
                  <span style={{ color: rsiColor(p.rsi2) }}>RSI(2): {p.rsi2?.toFixed(1) ?? '—'}</span>
                  <span style={{ color: rsiColor(p.rsi6) }}>RSI(6): {p.rsi6?.toFixed(1) ?? '—'}</span>
                  <span style={{ color: rsiColor(p.rsi14) }}>RSI(14): {p.rsi14?.toFixed(1) ?? '—'}</span>
                </div>
                <div className={styles.pickPrices}>
                  <span>
                    Entry: <strong>${p.entryPrice?.toFixed(2)}</strong>
                  </span>
                  {p.stopLoss != null && <span className={styles.neg}>Stop: ${p.stopLoss.toFixed(2)}</span>}
                  {p.takeProfit != null && <span className={styles.pos}>Target: ${p.takeProfit.toFixed(2)}</span>}
                </div>
                {p.holdDays != null && p.profitTargetPct != null && (
                  <div className={styles.pickHold}>
                    Hold: ~{p.holdDays}d · Target: {p.profitTargetPct}%
                  </div>
                )}
                <p className={styles.pickReason}>{p.reason}</p>
                <button
                  className={styles.tradeBtn}
                  onClick={() =>
                    navigate(`/quick-trade?symbol=${p.symbol}&action=${p.direction === 'LONG' ? 'buy' : 'short'}`)
                  }
                >
                  Trade {p.symbol}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Daily Edge</h1>
          <p className={styles.subtitle}>
            Automated S&amp;P 500 scans for RSI extremes — morning bounce candidates + overnight hold setups.
          </p>
        </div>
        <div className={styles.datePicker}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={styles.dateInput} />
        </div>
      </div>

      {loading && <SignalLoader message="Loading Daily Edge..." />}
      {error && <p className={styles.error}>{error}</p>}

      {data && !loading && (
        <div className={styles.panels}>
          {renderPanel(
            'Morning Picks',
            'Buy at the bell, ride the bounce, sell by lunch.',
            data.premarket,
            '9:15 AM',
            'premarket',
          )}
          {renderPanel(
            'Power Hour Picks',
            'Enter late, hold overnight, sell into next-morning strength.',
            data.powerHour,
            '3:45 PM',
            'power_hour',
          )}
        </div>
      )}
    </section>
  );
};

export default DailyEdge;
